import React, { useMemo } from "react";
import { areAllOptionsSet } from "theme/modules/AddToCart/areAllOptionsSet";
import { resolveSelectedOptions } from "theme/pages/Product/useSelectedProductWithConfigurableOptions";
/**
 * @typedef {object} InputProps
 * @property {import('./EnhanceAddToRequisitionList').InputProps["items"]} items
 */

/**
 * @typedef {Object<string, string>} NormalizedSelectedConfigurableOptions
 */

/**
 * @typedef {object} NormalizedBundleOptionValue
 * @property {string} quantity
 * @property {string[]} values
 */

/**
 * @typedef {Object<string, NormalizedBundleOptionValue>} NormalizedSelectedBundleOptions
 */

/**
 * @typedef {import('./EnhanceAddToRequisitionList').InputProps["items"][number]} Item
 */

/**
 * @typedef {object} NormalizedItem
 * @property {Item["product"]} product
 * @property {Item["quantity"]} quantity
 * @property {NormalizedSelectedConfigurableOptions} selectedConfigurableOptions
 * @property {NormalizedSelectedBundleOptions} selectedBundleOptions
 */

/**
 * @typedef {NormalizedItem[]} NormalizedItems
 */

/**
 * @typedef {object} OutputProps
 * @property {boolean} areAllOptionsSet
 * @property {NormalizedItems} items
 */

/**
 * @param {React.ComponentType<BaseProps & InputProps & OutputProps>} BaseComponent
 * @returns {React.FunctionComponent<BaseProps & InputProps>}
 * @template BaseProps
 */
const withAllOptionsSet = (BaseComponent) => (props) => {
  const { items } = props;

  const normalizedItems = useMemo(
    () =>
      items.map(
        ({
          product,
          quantity,
          selectedConfigurableOptions,
          selectedCustomOptions,
          selectedBundleOptions,
          ...rest
        }) => {
          return {
            product,
            quantity,
            selectedConfigurableOptions: resolveSelectedOptions(
              product.options,
              selectedConfigurableOptions
            ),
            selectedCustomOptions: resolveSelectedOptions(
              product.custom_options,
              selectedCustomOptions
            ),
            selectedBundleOptions: resolveSelectedOptions(
              product.bundleOptions,
              selectedBundleOptions
            ),
          };
        }
      ),
    [items]
  );

  const areAllItemsOptionsSet = useMemo(() => {
    return !normalizedItems.some(
      ({
        product,
        selectedConfigurableOptions,
        selectedCustomOptions,
        selectedBundleOptions,
      }) => {
        return (
          !product.options ||
          !areAllOptionsSet(
            product,
            selectedConfigurableOptions,
            selectedCustomOptions,
            selectedBundleOptions
          )
        );
      }
    );
  }, [normalizedItems]);

  return (
    <BaseComponent
      {...props}
      items={normalizedItems}
      areAllOptionsSet={areAllItemsOptionsSet}
    />
  );
};

export default withAllOptionsSet;
